// ============================================================
// PRIVACY — プライバシーポリシー（公開ページ・ログイン不要）。
// Google OAuth 審査（審査官は未ログインで閲覧する）と、メンバーへの
// データ取り扱い説明のための静的ページ。App.tsx で AuthGate の外
// （/privacy）に配置される。認証・adapter には一切依存しない。
// Google API Services User Data Policy（Limited Use）への準拠明記は
// 審査必須のため、§3 の文言を削らないこと。
// ============================================================
import { Link } from 'react-router-dom';

/** 見出し＋本文のセクション（読みものページの最小構成）。 */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-7">
      <h2 className="text-base font-bold text-charcoal">{title}</h2>
      <div className="mt-2 text-sm leading-relaxed text-slate-600 space-y-2">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-base text-charcoal px-4 py-10">
      <div className="mx-auto w-full max-w-2xl">
        {/* brand（Login と同トーン） */}
        <div className="flex items-center gap-3 mb-6">
          <div className="grid place-items-center w-10 h-10 rounded-soft bg-charcoal text-white text-base font-bold">
            T
          </div>
          <div>
            <p className="text-sm font-bold">TEMP 社内ポータル</p>
            <p className="text-2xs text-slate-sub">Team Portal</p>
          </div>
        </div>

        <div className="rounded-card border border-hair bg-white p-6 sm:p-8">
          <h1 className="text-xl font-bold text-ink">プライバシーポリシー</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            TEMP LLC（以下「当社」）は、当社が運営する社内メンバー向けツール
            「TEMP社内ポータル」（以下「本サービス」）における利用者情報の
            取り扱いについて、以下のとおり定めます。
          </p>

          <Section title="1. 取得する情報">
            <p>本サービスは、業務管理に必要な範囲で以下の情報を取得します。</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Google アカウントの基本情報（氏名・メールアドレス）</li>
              <li>Google Tasks のタスクデータ（同期表示・更新のため）</li>
              <li>Google Calendar の予定（読み取りのみ）</li>
              <li>社内で共有された議事録・タスク・ナレッジ</li>
            </ul>
          </Section>

          <Section title="2. 利用目的">
            <p>
              取得した情報は、社内の業務管理（タスク・予定の同期表示、議事録の共有）
              に限定して利用します。これ以外の目的で利用することはありません。
            </p>
          </Section>

          <Section title="3. Google API から取得したデータの扱い">
            <p>
              本サービスによる Google API から取得した情報の利用は、
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-deep hover:underline"
              >
                Google API Services User Data Policy
              </a>
              （Limited Use の要件を含む）に準拠します。
            </p>
            <p>
              Google API から取得したデータを第三者に販売すること、
              広告目的で利用することはありません。
            </p>
          </Section>

          <Section title="4. 情報の保存先とアクセス制限">
            <p>
              取得した情報は Google Firebase（Firestore）に保存します。
              保存された情報へのアクセスは、社内メンバーの権限（所属プロジェクト・役割）
              に応じて制限されます。
            </p>
          </Section>

          <Section title="5. トークンの取り扱い">
            <p>
              バックグラウンド同期のためのリフレッシュトークンは、暗号化された環境
              （Google Secret Manager / Firestore）で保管します。
            </p>
            <p>
              利用者は、いつでも Google アカウント設定（
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-deep hover:underline"
              >
                myaccount.google.com/permissions
              </a>
              ）から本サービスへのアクセス許可を取り消すことができます。
            </p>
          </Section>

          <Section title="6. お問い合わせ">
            <p>
              本ポリシーに関するお問い合わせは、以下までご連絡ください。
            </p>
            <p>
              運営者：TEMP LLC
              <br />
              メール：
              <a href="mailto:info@biz-tsunagu.com" className="text-sky-deep hover:underline">
                info@biz-tsunagu.com
              </a>
            </p>
          </Section>

          <p className="mt-8 pt-4 border-t border-hair font-num text-xs text-slate-sub">
            制定日：2026-07-10
          </p>
        </div>

        <p className="mt-5 text-center">
          <Link to="/" className="text-sm text-sky-deep hover:underline">
            ログイン画面へ戻る
          </Link>
        </p>
      </div>
    </div>
  );
}
